import {
  useContext, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, Spinner, Toast,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import { RiArrowLeftSLine } from '@remixicon/react';
import { useNavigate, useParams } from 'react-router-dom';

import { useFetchTicketById, useInsertTicketComment } from '../../gql';
import MessageBubble from './components/MessageBubble';
import MessageInput from './components/MessageInput';
import TicketHeader from './components/TicketHeader';
import messages from './messages';
import './Support.scss';

const ViewTicket = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const { authenticatedUser } = useContext(AppContext);

  const {
    ticket, loading, error, refetch,
  } = useFetchTicketById(ticketId);
  const { insertTicketComment, loading: sending } = useInsertTicketComment();

  const [toasts, setToasts] = useState([]);
  const nextToastIdRef = useRef(0);

  const showToast = (message, variant) => {
    if (!message) { return; }
    const id = nextToastIdRef.current;
    nextToastIdRef.current += 1;
    setToasts((prev) => [...prev, { id, message, variant }]);
  };

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const userId = authenticatedUser?.userId ?? authenticatedUser?.id;

  const learnerName = authenticatedUser?.name || authenticatedUser?.username || '';
  const supportLabel = intl.formatMessage(messages.supportBadge);

  const conversation = useMemo(() => {
    if (!ticket?.ticketUpdates) { return []; }
    return ticket.ticketUpdates.map((update) => {
      const isSelf = update.updatedBy === userId;
      return {
        id: update.id,
        sender: isSelf ? 'learner' : 'support',
        senderName: isSelf ? learnerName : supportLabel,
        content: update.comment,
        timestamp: update.createdAt,
        userImage: isSelf ? authenticatedUser?.profileImage?.imageUrlSmall : undefined,
      };
    });
  }, [ticket, userId, authenticatedUser, learnerName, supportLabel]);

  const handleSendMessage = (text) => {
    insertTicketComment({
      variables: {
        ticketId,
        updatedBy: userId,
        comment: text,
      },
      onCompleted: () => {
        refetch();
      },
      onError: () => {
        showToast(intl.formatMessage(messages.somethingWentWrong), 'error');
      },
    });
  };

  if (loading && !ticket) {
    return (
      <div className="learner-support-view learner-support-view--loading">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.viewLoading)} />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="learner-support-view">
        <Alert variant="danger" className="learner-support__alert">
          {intl.formatMessage(messages.ticketLoadError)}
        </Alert>
      </div>
    );
  }

  return (
    <div className="learner-support-view">
      <Button
        variant="tertiary"
        size="sm"
        iconBefore={RiArrowLeftSLine}
        onClick={() => navigate('/support')}
        className="learner-support-view__back"
      >
        {intl.formatMessage(messages.backToTickets)}
      </Button>

      <TicketHeader ticket={ticket} />

      <section className="learner-support-view__conversation">
        {conversation.length === 0 ? (
          <div className="learner-support-view__empty">
            {intl.formatMessage(messages.noMessages)}
          </div>
        ) : (
          conversation.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
      </section>

      <div className="learner-support-view__reply">
        <MessageInput
          onSendMessage={handleSendMessage}
          disabled={sending}
          replyToName={intl.formatMessage(messages.supportStaff)}
        />
      </div>

      {toasts.map((t) => (
        <Toast
          key={t.id}
          show
          variant={t.variant}
          onClose={() => dismissToast(t.id)}
        >
          {t.message}
        </Toast>
      ))}
    </div>
  );
};

export default ViewTicket;
